import usePrevious from 'helpers/hooks/usePrevious'
import useTranslate from 'helpers/hooks/useTranslate'
import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import CancelRequestError from 'requests/errors/cancelRequestError'
import InvalidEntityError from 'requests/errors/invalidEntityError'
import NotImplementedError from 'requests/errors/notImplementedError'
import UnauthorizedError from 'requests/errors/unauthorizedError'
import VacationWeek, { ErrorVacationWeek } from 'requests/objects/vacationWeek'
import Status from 'types/status'
import CompaniesHandler from 'requests/handlers/companiesHandler'

/**
 * useVacationWeekModal
 * @param {object} props Props
 * @param {Partial<VacationWeek>=} props.data VacationWeek data
 * @param {boolean} props.isVisible isVisible
 * @param {boolean} [props.isDeletion] isDeletion
 * @param {object} props.lang Lang
 * @param {CompaniesHandler} props.handler handler
 * @param {(vacationWeek: VacationWeek, isDeletion?: boolean) => void} props.onChange onChange
 * @param {number} props.companyId companyId
 * @returns {{
 *  tKey: (key: import('types/translations').TranslationsType, args?: { [key: string]: string; }) => string,
 *  tObj: (obj: import('types/translations').TranslationsObjectType | string | [string], args?: { [key: string]: string; }) => string,
 *  errorMessage: string,
 *  setErrorMessage: React.Dispatch<React.SetStateAction<string>>,
 *  onSubmit: () => Promise<void>,
 *  deleteVacationWeek: () => Promise<void>,
 *  vacationWeek: VacationWeek,
 *  setVacationWeek: React.Dispatch<React.SetStateAction<VacationWeek>>,
 *  errorField: ErrorVacationWeek,
 *  status: string,
 *  dialogTitle: string,
 * }} Returns
 */
export default function useVacationWeekModal({
    lang,
    data,
    handler,
    isVisible,
    isDeletion = false,
    onChange,
    companyId,
}) {
    const { tKey, tObj } = useTranslate({ lang })

    const [errorMessage, setErrorMessage] = useState('')
    const [errorField, setErrorField] = useState(new ErrorVacationWeek())
    const [status, setStatus] = useState(Status.IDLE)
    const [vacationWeek, setVacationWeek] = useState(new VacationWeek(data))

    /** @type {React.MutableRefObject<import('requests/apiHandler').RequestApi<VacationWeek>>} */
    const handlerUpsert = useRef()

    /** @type {React.MutableRefObject<import('requests/apiHandler').RequestApi<VacationWeek>>} */
    const handlerDelete = useRef()

    const prevIsVisible = usePrevious(isVisible)

    // Set logistic constraint data for edition or creation. Ensure that data in modal are up to date
    useEffect(() => {
        if (prevIsVisible !== isVisible)

            if (isVisible) { setVacationWeek(prevState => ({ ...prevState, ...data })) } else {
                setErrorMessage(null)
                setVacationWeek(new VacationWeek({ companyId }))
            }
    }, [companyId, data, isVisible, prevIsVisible])

    /**
     * Save the address
     */
    const onSubmit = useCallback(async () => {
        try {
            setStatus(Status.PENDING)
            handlerUpsert.current = handler.upsertVacationWeek(companyId, vacationWeek, data?.vacationWeekId)
            const res = await handlerUpsert.current.fetch()

            onChange(res)
            setStatus(Status.RESOLVED)
        } catch (error) {
            setStatus(Status.REJECTED)
            switch (error?.constructor) {
                case CancelRequestError:
                case UnauthorizedError: break
                case NotImplementedError: break
                case InvalidEntityError:
                    // eslint-disable-next-line no-case-declarations
                    const err = /** @type {InvalidEntityError<ErrorVacationWeek>} */(error)
                    setErrorField(err.errorField)
                    setErrorMessage(err.message)
                    break
                default:
                    setErrorMessage(error.message || error)
                    // eslint-disable-next-line no-console
                    console.error(error)
                    break
            }
        }
    }, [handler, companyId, vacationWeek, data?.vacationWeekId, onChange])

    /**
     * Delete the logistic constraint
     */
    const deleteVacationWeek = useCallback(async () => {
        try {
            setStatus(Status.PENDING)
            handlerDelete.current = handler.removeVacationWeekById(companyId, data?.vacationWeekId)
            const res = await handlerDelete.current.fetch()

            onChange(res, true)
            setStatus(Status.RESOLVED)
        } catch (error) {
            setStatus(Status.REJECTED)
            switch (error?.constructor) {
                case CancelRequestError:
                case UnauthorizedError:
                case NotImplementedError:
                case InvalidEntityError:
                    break
                default:
                    setErrorMessage(error.message || error)
                    break
            }
        }
    }, [companyId, data?.vacationWeekId, handler, onChange])

    const dialogTitle = useMemo(() => {
        if (isDeletion)
            return tKey('deleteVacationWeek')

        if (vacationWeek.vacationWeekId)
            return tKey('modalEditVacationWeekTitle')

        return tKey('modalVacationWeekTitle')
    }, [isDeletion, tKey, vacationWeek.vacationWeekId])

    // Clean some stuff
    useEffect(() => {
        if (isVisible)
            setErrorField(new ErrorVacationWeek())
    }, [isVisible])

    // On component did unmount
    useEffect(() => () => {
        handlerUpsert.current?.cancel()
        handlerDelete.current?.cancel()
    }, [])

    return {
        tKey,
        tObj,
        errorMessage,
        setErrorMessage,
        onSubmit,
        deleteVacationWeek,
        vacationWeek,
        setVacationWeek,
        errorField,
        status,
        dialogTitle,
    }
}
